import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { AlertTriangle, ArrowLeft, Check, ArrowRight, Paperclip } from "lucide-react";
import parse from "html-react-parser";
import Nav from "../Components/Nav/Nav";
import Confetti from "../Components/FormationLearner/Confetti";
import CurriculumSidebar from "../Components/FormationLearner/CurriculumSidebar";
import Login from "./Login";
import { UidContext } from "../Components/App.Context";
import { getLearnFormation, completeLesson } from "../actions/formationLearner.actions";

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => a.order - b.order);

function toEmbedUrl(url) {
  if (!url) return "";
  try {
    if (url.includes("youtube.com/watch")) {
      const u = new URL(url);
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    // YouTube Shorts : youtube.com/shorts/ID → embed
    if (url.includes("youtube.com/shorts/")) {
      const id = url.split("shorts/")[1].split(/[?&/]/)[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("loom.com/share/")) return url.replace("/share/", "/embed/");
    // Vimeo : vimeo.com/123456789, .../channels/x/123456789, ou non répertorié
    // vimeo.com/123456789/abcdef (hash dans le chemin) ou ?h=abcdef
    //   → player.vimeo.com/video/123456789(?h=abcdef)
    if (url.includes("vimeo.com/")) {
      // déjà au format player ? on laisse tel quel
      if (url.includes("player.vimeo.com/")) return url;
      const u = new URL(url);
      const parts = u.pathname.split("/").filter(Boolean);
      const idIdx = parts.findIndex((p) => /^\d+$/.test(p));
      if (idIdx >= 0) {
        const id = parts[idIdx];
        const next = parts[idIdx + 1];
        const h =
          u.searchParams.get("h") || (next && /^[a-z0-9]+$/i.test(next) ? next : null);
        return `https://player.vimeo.com/video/${id}${h ? `?h=${h}` : ""}`;
      }
    }
    return url;
  } catch (e) {
    return url;
  }
}

const FormationLesson = ({ socket }) => {
  const { id, lessonId } = useParams();
  const uid = useContext(UidContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current } = useSelector((s) => s.formationLearnerReducer);
  const [celebrate, setCelebrate] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    dispatch(getLearnFormation(id)).catch(() => {
      if (active) setLoadError(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch, id]);

  if (!uid) return <Login />;

  if (!current || !current.formation || String(current.formation.id) !== String(id)) {
    if (loadError) {
      return (
        <>
          <Nav socket={socket} />
          <div className="mainContainer">
            <div className="lesson-fullscreen">
              <div className="quiz-locked-notice">
                <AlertTriangle size={40} />
                <p>Formation indisponible. Elle a peut-être été dépubliée ou supprimée.</p>
                <Link to="/formations" className="greenButton">
                  <ArrowLeft size={15} /> Retour aux formations
                </Link>
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <div className="lesson-fullscreen">
            <p style={{ padding: "2rem" }}>Chargement…</p>
          </div>
        </div>
      </>
    );
  }

  const { formation, progress } = current;
  const modules = sortByOrder(formation.FormationModules);
  const orderedLessons = modules.flatMap((m) => sortByOrder(m.FormationLessons));
  const idx = orderedLessons.findIndex((l) => String(l.id) === String(lessonId));
  const lesson = idx >= 0 ? orderedLessons[idx] : null;
  const completedSet = new Set(progress.completedLessonIds || []);
  const next = orderedLessons[idx + 1];

  const markDone = async () => {
    try {
      const r = await dispatch(completeLesson(lesson.id));
      await dispatch(getLearnFormation(id));
      if (r && r.completion && r.completion.complete) {
        setCelebrate(true);
        toast.success("Formation terminée — badge obtenu !");
      } else toast.success("Leçon terminée");
    } catch (e) {
      toast.error("Action impossible");
    }
  };

  const goNext = () => {
    if (next) navigate(`/formations/${id}/apprendre/${next.id}`);
    else navigate(`/formations/${id}`);
  };

  if (!lesson) {
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <div className="lesson-fullscreen">
            <p style={{ padding: "2rem" }}>Leçon introuvable.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {celebrate ? <Confetti onDone={() => setCelebrate(false)} /> : null}
      <ToastContainer />
      <Nav socket={socket} />
      <div className="mainContainer">
        <div className="lesson-fullscreen">
          <div className="lesson-layout">
            <CurriculumSidebar formation={formation} progress={progress} active={{ type: "lesson", id: lessonId }} />

            <main className="lesson-main">
              <p className="lesson-eyebrow">
                Leçon {idx + 1} sur {orderedLessons.length}
              </p>
              <h1>{lesson.title}</h1>
              {(Array.isArray(lesson.blocks) ? lesson.blocks : []).map((b) => (
                <div className="lesson-block" key={b.id}>
                  {b.type === "text" && <div className="lesson-text">{parse(b.html || "")}</div>}
                  {b.type === "video" && b.url && (
                    <div className="lesson-video">
                      <iframe
                        src={toEmbedUrl(b.url)}
                        title="video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  )}
                  {b.type === "image" && b.url && <img className="lesson-image" src={b.url} alt={b.alt || ""} />}
                  {b.type === "file" && b.url && (
                    <a className="lesson-file" href={b.url} target="_blank" rel="noreferrer">
                      <Paperclip size={16} /> {b.name || "Télécharger le fichier"}
                    </a>
                  )}
                </div>
              ))}

              <div className="lesson-actions">
                <button className="greenButton" onClick={markDone}>
                  <Check size={15} /> {completedSet.has(lesson.id) ? "Terminé" : "Marquer comme terminé"}
                </button>
                <button className="blackButton" onClick={goNext}>
                  {next ? "Leçon suivante" : "Retour à la formation"} <ArrowRight size={15} />
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormationLesson;
